export type MetricConfig = {
  id: string;
  label: string;
  description?: string;
  /** Which source this metric comes from. Absent = Keitaro. Drives the
   *  Keitaro/GSC source tabs in the Clicks grid. */
  source?: "keitaro" | "gsc";
};

export type MetricGroup = {
  id: string;
  title: string;
  description: string;
  metrics: MetricConfig[];
};

/** How an attribute reads: text, an ISO date, a USD amount, a count, yes/no. */
export type AttributeKind = "text" | "date" | "usd" | "int" | "bool";
export type AttributeColumn = { id: string; label: string; description: string; kind: AttributeKind };
/**
 * Columns that are fields of the row's domain, not figures measured on it.
 * Each source is one RPC returning {normalized domain: [values in `columns`
 * order]}, merged onto rows grouped by Domain or URL on the page (Clicks.tsx);
 * the grid draws them as attribute cells. A new source is one entry here and
 * one function on the server.
 */
export type AttributeSource = { id: string; title: string; description: string; rpc: string; columns: AttributeColumn[] };

/**
 * The Domain Tracker fields of a row's domain (owner's list of 27.10, in its
 * order), from get_domain_attributes (20261103123525) in exactly this order.
 */
export const DOMAIN_TRACKER_COLUMNS: AttributeColumn[] = [
  { id: "dt_status", label: "Status", description: "Domain Tracker: Status", kind: "text" },
  { id: "dt_expired", label: "Expired", description: "Domain Tracker: when the registration expires", kind: "date" },
  { id: "dt_renewal_cost", label: "Renewal Cost USD", description: "Domain Tracker: Domain Renewal Cost USD", kind: "usd" },
  { id: "dt_type", label: "Type", description: "Domain Tracker: Type (Brand, Slot, Trustpilot, Info…)", kind: "text" },
  { id: "dt_cms", label: "CMS", description: "Domain Tracker: CMS (Jetsik, HTML, WP…)", kind: "text" },
  { id: "dt_redirect", label: "301 Redirect", description: "Domain Tracker: 301 Redirect - the domain it redirects to", kind: "text" },
  { id: "dt_domain_added", label: "Domain Added", description: "Domain Tracker: Domain Added Date", kind: "date" },
  { id: "dt_purchase", label: "Purchase Date", description: "Domain Tracker: Purchase Date", kind: "date" },
  { id: "dt_date_to_ns", label: "Date to NS", description: "Domain Tracker: Date to NS", kind: "date" },
  { id: "dt_added_jetsik", label: "Added to Jetsik", description: "Domain Tracker: Date Added to Jetsik (the team's date; Jetsik's own may differ)", kind: "date" },
  { id: "dt_added_html", label: "Added HTML", description: "Domain Tracker: Date Added HTML", kind: "date" },
  { id: "dt_activation", label: "Activation Date", description: "Domain Tracker: Activation Date", kind: "date" },
  // More of the sheet with data (prod 03.11: 86-99% of 30-day revenue), appended
  // to the same array (20261103 attribute sources) - beyond the owner's 12.
  { id: "dt_brand_keyword", label: "Brand Keyword", description: "Domain Tracker: the brand keyword (keyword volume reads it)", kind: "text" },
  { id: "dt_keyword", label: "Keyword", description: "Domain Tracker: Keyword", kind: "text" },
  { id: "dt_geo", label: "GEO (DT)", description: "Domain Tracker: GEO", kind: "text" },
  { id: "dt_lang", label: "Language (DT)", description: "Domain Tracker: Language", kind: "text" },
  { id: "dt_domain_type", label: "Domain Type", description: "Domain Tracker: Type 2 (New, Drop…)", kind: "text" },
  { id: "dt_batch", label: "Batch", description: "Domain Tracker: Batch", kind: "text" },
  { id: "dt_keitaro_campaign", label: "Keitaro Campaign ID (DT)", description: "Domain Tracker: Keitaro campaign id", kind: "text" },
  { id: "dt_purchase_purpose", label: "Purchase Purpose", description: "Domain Tracker: Domain purchase purpose", kind: "text" },
  { id: "dt_live", label: "Live", description: "Domain Tracker: Live", kind: "bool" },
  { id: "dt_indexed", label: "Indexed (DT)", description: "Domain Tracker: Index (the team's tick; Indexation / DMCA has the checker's)", kind: "bool" },
  { id: "dt_ns", label: "NS", description: "Domain Tracker: NS", kind: "bool" },
  { id: "dt_gsc_connected", label: "GSC Connected", description: "Domain Tracker: GSC", kind: "bool" },
];

/**
 * The Jetsik fields of a row's domain (get_jetsik_attributes, in its order):
 * the CMS the sites run on. Brand is its own column (Attributes).
 */
export const JETSIK_COLUMNS: AttributeColumn[] = [
  { id: "jk_status", label: "Status (Jetsik)", description: "Jetsik: the site's status (indexed, indexation, need_content, redirected, blocked…)", kind: "text" },
  { id: "jk_team", label: "Team", description: "Jetsik: Team", kind: "text" },
  { id: "jk_geo", label: "GEO (Jetsik)", description: "Jetsik: the site's country", kind: "text" },
  { id: "jk_language", label: "Language (Jetsik)", description: "Jetsik: the site's language", kind: "text" },
  { id: "jk_template", label: "Template", description: "Jetsik: Template", kind: "text" },
  { id: "jk_type", label: "Type (Jetsik)", description: "Jetsik: Dynamic or Not connected", kind: "text" },
  { id: "jk_monitoring", label: "Monitoring", description: "Jetsik: Monitoring (daily, freeze, minute)", kind: "text" },
  { id: "jk_pages", label: "Pages", description: "Jetsik: number of pages", kind: "int" },
  { id: "jk_search_allow", label: "Search Allowed", description: "Jetsik: search engines allowed", kind: "bool" },
  { id: "jk_ready", label: "Ready", description: "Jetsik: Ready", kind: "bool" },
  { id: "jk_direct_deny", label: "Direct Deny", description: "Jetsik: Direct deny", kind: "bool" },
  { id: "jk_open_graph", label: "Open Graph", description: "Jetsik: Open Graph on", kind: "bool" },
  { id: "jk_created", label: "Created in Jetsik", description: "Jetsik: when the site was created (Jetsik's own date; Domain Tracker has the team's)", kind: "date" },
  { id: "jk_updated", label: "Updated in Jetsik", description: "Jetsik: last change of the site", kind: "date" },
  { id: "jk_age", label: "Age, days", description: "Days since the site was created in Jetsik", kind: "int" },
];

/** The Indexation page's checks of a row's domain (get_indexation_attributes, in its order). */
export const INDEXATION_COLUMNS: AttributeColumn[] = [
  { id: "ix_index", label: "Index Status", description: "Indexation: indexed or not, at the last check", kind: "text" },
  { id: "ix_dmca", label: "DMCA Status", description: "Indexation: DMCA or clean, at the last check", kind: "text" },
  { id: "ix_tier", label: "Tier", description: "Indexation: check tier (1, 2; 3 = no human clicks, not checked)", kind: "int" },
  { id: "ix_checked", label: "Last Checked", description: "Indexation: the domain's last check", kind: "date" },
  { id: "ix_dmca_since", label: "DMCA Since", description: "Indexation: flagged since", kind: "date" },
  { id: "ix_not_indexed_since", label: "Not Indexed Since", description: "Indexation: out of the index since", kind: "date" },
  { id: "ix_notices", label: "DMCA Notices", description: "Indexation: notices at the last check", kind: "int" },
  { id: "ix_urls", label: "Tracked URLs", description: "Indexation: the domain's URLs it checks", kind: "int" },
  { id: "ix_urls_not_indexed", label: "URLs Not Indexed", description: "Indexation: of the checked ones, not indexed at their last check; empty while none was checked", kind: "int" },
  { id: "ix_urls_dmca", label: "URLs with DMCA", description: "Indexation: of the checked ones, flagged at their last check; empty while none was checked", kind: "int" },
];

export const ATTRIBUTE_SOURCES: AttributeSource[] = [
  {
    id: "domain_tracker",
    title: "Domain Tracker",
    description: "The domain's row in the SEO team's Domain Tracker - for rows grouped by Domain or URL",
    rpc: "get_domain_attributes",
    columns: DOMAIN_TRACKER_COLUMNS,
  },
  {
    id: "jetsik",
    title: "Jetsik",
    description: "The site in Jetsik - for rows grouped by Domain or URL",
    rpc: "get_jetsik_attributes",
    columns: JETSIK_COLUMNS,
  },
  {
    id: "indexation",
    title: "Indexation / DMCA",
    description: "The Indexation page's checks of the domain - for rows grouped by Domain or URL",
    rpc: "get_indexation_attributes",
    columns: INDEXATION_COLUMNS,
  },
];
/**
 * Facts about the domain itself (its index and DMCA checks): on a URL row they
 * would read as the URL's, so these show only on rows with a domain part.
 */
export const DOMAIN_ONLY_ATTRIBUTES = new Set(INDEXATION_COLUMNS.map((c) => c.id));
/** Every attribute column by id. */
export const ATTRIBUTE_COLUMNS = new Map(ATTRIBUTE_SOURCES.flatMap((s) => s.columns.map((c) => [c.id, c] as const)));

export const METRICS_CONFIG: MetricGroup[] = [
  {
    id: "clicks",
    title: "Clicks",
    description: "Click volume and unique visitor metrics",
    metrics: [
      { id: "total_clicks", label: "Clicks", description: "Total clicks" },
      // not_bots under its plain name: clicks left after Oracul's bot detection
      // (the Bots preset picked on Clicks, else the global rules in Settings).
      { id: "not_bots", label: "Clicks (Oracul)", description: "Clicks after Oracul bot detection: minus bots by the Bots preset, else by the global rules (Settings → Bot Detection)" },
      { id: "unique_campaign", label: "Unique (Campaign)", description: "Unique by campaign" },
      { id: "unique_stream", label: "Unique (Stream)", description: "Unique by stream" },
      { id: "unique_global", label: "Unique (Global)", description: "Globally unique" },
      { id: "lp_clicks", label: "LP Clicks", description: "Landing page clicks" },
      { id: "lp_ctr_percent", label: "LP CTR %", description: "Landing page click-through rate" },
    ],
  },
  {
    id: "conversions",
    title: "Conversions",
    description: "Conversion volume and approval metrics",
    metrics: [
      { id: "total_conversions", label: "Conversions", description: "Keitaro conversions = Leads + Sales (* Registrations)" },
      { id: "total_leads", label: "Leads", description: "Lead conversions" },
      { id: "total_sales", label: "Sales", description: "Sale conversions" },
      // Rejected and Rebills removed 06.10.2026 (owner): both ~0 over 30 days.
    ],
  },
  {
    id: "rates",
    title: "Rates",
    description: "Conversion and performance rates",
    metrics: [
      { id: "cr_percent", label: "CR %", description: "Conversion rate" },
      { id: "cr_leads_percent", label: "CR Leads %", description: "Lead conversion rate" },
      { id: "cr_sales_percent", label: "CR Sales %", description: "Sales conversion rate" },
      // ROI Confirmed % removed 06.10.2026 (owner): needs rejected_revenue + cost, both 0.
    ],
  },
  {
    // One Money group (owner's call 30.09.2026). The others were merged in and
    // the dead metrics dropped: measured over 30 days `cost`, `rejected` and
    // `rejected_revenue` are all zero, so CPC (=cost/clicks) is always $0, and
    // eCPC / EPC Confirmed / eCPM Confirmed are byte-for-byte equal to EPC/eCPM
    // (their only difference is subtracting rejected_revenue, which is 0). What
    // is left are the four that actually vary: Revenue, EPC, uEPC, eCPM. Bring
    // the cost/confirmed ones back only when real cost or rejection data flows.
    id: "money",
    title: "Money",
    description: "Revenue and per-click earnings",
    metrics: [
      { id: "total_revenue", label: "Revenue", description: "Total revenue" },
      { id: "epc", label: "EPC", description: "Earnings per click" },
      {
        id: "uepc",
        label: "uEPC",
        description: "Earnings per unique visitor, not per click",
      },
      { id: "ecpm", label: "eCPM", description: "Revenue per 1000 clicks" },
    ],
  },
  {
    id: "attributes",
    title: "Attributes",
    description: "Columns that describe the row rather than measure it",
    metrics: [
      {
        id: "brand",
        label: "Brand",
        description: "Brand behind the domain, from Jetsik",
      },
      {
        id: "campaign_group",
        label: "Campaign Group",
        description: "Keitaro campaign group(s) behind the row",
      },
    ],
  },
  ...ATTRIBUTE_SOURCES.map((s) => ({
    id: s.id,
    title: s.title,
    description: s.description,
    metrics: s.columns.map(({ id, label, description }) => ({ id, label, description })),
  })),
  {
    id: "quality",
    title: "Quality",
    description: "Traffic quality and fraud detection",
    metrics: [
      { id: "total_bots", label: "Bots (Keitaro)", description: "Bot clicks — Keitaro's own bot flag" },
      { id: "bot_share_percent", label: "Bot Share % (Keitaro)", description: "Share of bot clicks — Keitaro logic" },
      // Our own verdict (bot_rules, migrations 20261019170000 / 20261021130000):
      // the rules of the Bots preset picked on Clicks, else the global rules
      // from Settings. A dash means the period holds days without bot data.
      { id: "bots_oracul", label: "Bots (Oracul)", description: "Bot clicks by our rules: the Bots preset, else Settings → Bot Detection" },
      { id: "bot_share_oracul_percent", label: "Bot Share % (Oracul)", description: "Share of bot clicks by our rules" },
    ],
  },
  {
    id: "keitaro",
    title: "Keitaro",
    description: "More Keitaro metrics, as Keitaro Toolkit shows them",
    // Derived in the browser from the row's own counts (DERIVED_METRICS in
    // clicksGridEngine.ts), so they work under any grouping and in compare.
    metrics: [
      { id: "approve_percent", label: "Approve %", description: "Sales / conversions" },
      { id: "revenue_per_sale", label: "Revenue / Sale", description: "Average payout per sale" },
      { id: "revenue_per_conversion", label: "Revenue / Conversion", description: "Revenue per conversion (registration or sale)" },
      { id: "unique_percent", label: "Unique % (Global)", description: "Unique (Global) / clicks" },
      // Keitaro Toolkit's UC Campaign / UC Stream Rate.
      { id: "unique_campaign_percent", label: "Unique % (Campaign)", description: "Unique (Campaign) / clicks" },
      { id: "unique_stream_percent", label: "Unique % (Stream)", description: "Unique (Stream) / clicks" },
      { id: "uepc_campaign", label: "uEPC (Campaign)", description: "Revenue per visitor unique within the campaign" },
      // Clicks each bot signal marks (get_dynamic_stats, 20261027193330), whether
      // its rule is on or not: what that signal alone would hide - counted among
      // the clicks the report shows, so 0 while the Bots preset hides that signal.
      { id: "bots_ua", label: "Bots: User agent", description: "Clicks the user-agent list signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
      { id: "bots_isp", label: "Bots: Datacenter ISP", description: "Clicks the datacenter / hosting ISP signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
      { id: "bots_device", label: "Bots: Mobile + desktop OS", description: "Clicks the mobile device with a desktop OS signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
      { id: "bots_ip_burst", label: "Bots: IP 20+ clicks/day", description: "Clicks the IP with 20+ clicks a day signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
      { id: "bots_ip_visitors", label: "Bots: IP 10+ visitors/day", description: "Clicks the IP with 10+ visitors a day signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
      { id: "bots_language", label: "Bots: Language unknown", description: "Clicks the language unknown signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
      { id: "bots_no_browser", label: "Bots: Browser unknown", description: "Clicks the browser not recognised signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
      { id: "bots_old_chrome", label: "Bots: Chrome < 110", description: "Clicks the Chrome older than 110 signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
      { id: "bots_proxy", label: "Bots: Proxy / VPN", description: "Clicks the Keitaro proxy flags signal marks, whether its rule is on or not; among the clicks shown, so 0 while the Bots preset hides this signal" },
    ],
  },
  {
    id: "gsc",
    title: "GSC (Search Console)",
    description: "Organic search metrics from Google Search Console, joined by domain",
    metrics: [
      { id: "gsc_clicks", label: "GSC Clicks", description: "Organic search clicks (GSC)", source: "gsc" },
      { id: "gsc_impressions", label: "Impressions", description: "Search impressions (GSC)", source: "gsc" },
      { id: "gsc_ctr", label: "GSC CTR %", description: "Search click-through rate (GSC)", source: "gsc" },
      { id: "gsc_position", label: "Avg Position", description: "Average search position (GSC)", source: "gsc" },
      // GSC clicks per 30 days / the row's monthly keyword volume.
      { id: "capture_rate", label: "Capture Rate %", description: "Share of the brand's monthly searches this domain gets as GSC clicks (clicks per 30 days / keyword volume)", source: "gsc" },
    ],
  },
  {
    // keyword volume: keywordtool exports joined through Domain Tracker's
    // brand_keyword; only for rows grouped by Brand, Domain or URL.
    id: "keyword_volume",
    title: "Keyword Volume",
    description: "Monthly search volume of the row's brand keyword (keywordtool)",
    metrics: [
      { id: "kv_sparkline", label: "Volume 12m (chart)", description: "Last 12 months as a sparkline; M1–M12 expand" },
      { id: "kv_last", label: "Volume (last month)", description: "Volume in the row's latest month with data" },
      { id: "kv_avg", label: "Volume Avg 12m", description: "Average monthly volume over the last 12 months with data" },
      { id: "kv_trend_percent", label: "Volume Trend 3v3 %", description: "Last 3 months vs the 3 before, as keywordtool's Trend (3vs3)" },
    ],
  },
  {
    // keyword volume: one metric per calendar month, each sortable, filterable
    // and chartable on its own; grouped so the picker can take all 12 at once.
    // Labels become calendar months ("Volume Aug 2026") via metricLabel().
    id: "keyword_volume_months",
    title: "Keyword Volume · by month",
    description: "Volume per calendar month, newest uploaded month first",
    metrics: Array.from({ length: 12 }, (_, i) => ({
      id: `kv_m${i + 1}`,
      label: `Volume M${i + 1}`,
      description: i === 0 ? "Newest uploaded month" : `${i} month${i > 1 ? "s" : ""} before the newest`,
    })),
  },
];

// Every numeric metric a custom-metric formula may reference — the whole metric
// registry minus the text/visual columns. Derived metrics (epc, uepc, cr_*,
// gsc_*, kv_*) are included: each is a number on the row (uepc is filled in by
// the precompute), so a formula can build on them, not just on raw counts.
// Derived in the browser and not on the row a formula reads (uEPC is passed in
// separately): a formula over them would read 0.
const CUSTOM_FORMULA_EXCLUDE = new Set([
  "brand", "campaign_group", "kv_sparkline", ...ATTRIBUTE_COLUMNS.keys(),
  "approve_percent", "revenue_per_sale", "revenue_per_conversion", "unique_percent", "uepc_campaign",
  "unique_campaign_percent", "unique_stream_percent",
]);
export const CUSTOM_METRIC_FIELDS: { id: string; label: string }[] =
  METRICS_CONFIG.flatMap((g) => g.metrics)
    .filter((m) => !CUSTOM_FORMULA_EXCLUDE.has(m.id))
    .map((m) => ({ id: m.id, label: m.label }));

export const DEFAULT_VISIBLE_METRICS = [
  "brand",
  "total_clicks",
  "not_bots",
  // Leads off by default (owner, 08.10.2026): Conversions already covers it
  // (= Leads + Sales); add Leads back via the Metrics picker when needed.
  "total_conversions",
  "total_sales",
  "cr_percent",
  "total_revenue",
  "epc",
  // uEPC alongside EPC rather than instead of it: with 72.7% of clicks coming
  // from bots that click ~12 times each (measured 29.09.2026), EPC is deflated
  // several-fold on affected domains while uEPC barely moves. Seeing the two
  // side by side is what makes that visible.
  "uepc",
  // GSC block — visible under the GSC source tab (fills only for domains GSC covers).
  "gsc_clicks",
  "gsc_impressions",
  "gsc_ctr",
  "gsc_position",
  // keyword volume: the sparkline column was always on before it became a
  // metric (30.09.2026); keep it on by default, with the last month's figure.
  "kv_sparkline",
  "kv_last",
];